import { AlertColor, AlertPropsColorOverrides } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import { makeAutoObservable, runInAction } from "mobx";
import { MutableRefObject, useRef } from "react";

import { NavigateOptions } from "react-router-dom";
import { BackButtonProps } from "./components/common/BackButton/BackButton.tsx";
import { ExportImageProps } from "./components/common/ExportImagePopup/ExportImagePopup.tsx";
import { IRootStore } from "./mobx/root-store.tsx";
import { isBriaMember, isBriaOrg } from "./utils/index.ts";

export interface IUIStore {
	sideBar: MutableRefObject<null | HTMLDivElement>;
	guideSectionContainerRef: MutableRefObject<null | HTMLDivElement>;
	backButton?: BackButtonProps;
	checkoutPopupSelectedPriceId: string | undefined;
	openCheckoutPopup: boolean;
	openPricingPopup: boolean;
	opensaveNewTemplatePopup: boolean;
	SubscriptionLinkGeneratorPopup: boolean;
	OrgInfoPopup: boolean;
	leadSourcePopup: boolean;
	ExportImagePopup: boolean;
	exportImageProps: ExportImageProps;
	ApiLimitReachedPopup: boolean;
	ApiLimitReachedBanner: boolean;
	InvalidSubscriptionBanner: boolean;
	InvalidSubscriptionPopup: boolean;
	iframeAdsEditorInfoPopup: boolean;
	selectedGuideIndex: number;
	showGuideSection: boolean;
	showGuideToolTip: boolean;
	isNewModelBannerVisible: boolean;
	openCommonRequestAccessPopup: boolean;
	showInviteMembersPopup: boolean;
	snackBarProps?: {
		open: boolean;
		severity: OverridableStringUnion<AlertColor, AlertPropsColorOverrides> | undefined;
		message: string;
		autoHideDuration?: number;
	};

	// Global error alert with code
	errorAlertWithCode?: {
		open: boolean;
		title?: string;
		code?: string | number;
		message?: string;
		autoHideDuration?: number;
	};
	isTrainingPopupOpen: boolean;

	showExportImagePopup(exportImageProps?: ExportImageProps): void;

	showSideBar(): void;

	hideSideBar(): void;

	showDialog(targetDialog: keyof IUIStore): void;

	hideDialog(targetDialog: keyof IUIStore): void;

	showSnackbar(targetDialog: keyof IUIStore): void;

	hideSnackbar(targetDialog: keyof IUIStore): void;

	showRefElement(targetElement: keyof IUIStore): void;

	hideRefElement(targetElement: keyof IUIStore): void;
	hideAllFeedbackBoxes(event: any): void;

	showBackButton(
		to?: string,
		navigateOptions?: NavigateOptions,
		text?: string,
		showWarning?: boolean,
		loadingWarning?: boolean,
		onWarningConfirm?: () => Promise<void>,
		currentAppName?: string,
		warningTitle?: string,
		warningDescription?: string,
		confirmText?: string,
		cancelText?: string,
		cancelClosesOnly?: boolean,
		hideWarningTitle?: boolean,
	): void;

	hideBackButton(): void;

	CreateOrganizationDialog: boolean;

	setProperty<K extends keyof IUIStore>(key: K, value: IUIStore[K]): void;

	hideDesignEditorContainer(): void;

	showDesignEditorContainer(): void;

	showSnackBarAlert(
		severity: OverridableStringUnion<AlertColor, AlertPropsColorOverrides> | undefined,
		message: string,
		autoHideDuration?: number,
	): void;
	showErrorAlertWithCode(code?: string | number, message?: string, title?: string, autoHideDuration?: number): void;

	hideSnackBarAlert(): void;
	hideErrorAlertWithCode(): void;
	shouldDisplayCustomerViewBanner(): boolean;

	EditProjectDialog: boolean;
	EditSessionDialog: boolean;
	AddSessionDialog: boolean;
	TgEvaluationTutorialDialog: boolean;
}

export default class UIStore implements IUIStore {
	rootStore: IRootStore;
	sideBar = useRef<null | HTMLDivElement>(null);
	guideSectionContainerRef = useRef<null | HTMLDivElement>(null);
	backButton?: BackButtonProps;
	openCheckoutPopup: boolean = false;
	openPricingPopup: boolean = false;
	opensaveNewTemplatePopup: boolean = false;
	SubscriptionLinkGeneratorPopup: boolean = false;
	OrgInfoPopup: boolean = false;
	leadSourcePopup: boolean = false;
	ExportImagePopup: boolean = false;
	exportImageProps: ExportImageProps = {};
	ApiLimitReachedPopup: boolean = true;
	ApiLimitReachedBanner: boolean = true;
	InvalidSubscriptionBanner: boolean = true;
	InvalidSubscriptionPopup: boolean = true;
	iframeAdsEditorInfoPopup: boolean = true;
	selectedGuideIndex: number = 0;
	showGuideSection: boolean = false;
	showGuideToolTip: boolean = false;
	isNewModelBannerVisible: boolean = true;
	openCommonRequestAccessPopup: boolean = false;
	showInviteMembersPopup: boolean = false;
	checkoutPopupSelectedPriceId: string | undefined = undefined;
	CreateOrganizationDialog = false;
	EditProjectDialog = false;
	EditSessionDialog = false;
	AddSessionDialog = false;
	TgEvaluationTutorialDialog = false;
	isTrainingPopupOpen = false;

	snackBarProps?: {
		open: boolean;
		severity: OverridableStringUnion<AlertColor, AlertPropsColorOverrides> | undefined;
		message: string;
		autoHideDuration?: number;
	} = undefined;

	// Error alert state
	errorAlertWithCode?: {
		open: boolean;
		title?: string;
		code?: string | number;
		message?: string;
		autoHideDuration?: number;
	} = undefined;

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
	}

	showExportImagePopup = (exportImageProps: ExportImageProps = {}) => {
		runInAction(() => {
			this.ExportImagePopup = true;
			this.exportImageProps = exportImageProps;
		});
	};

	showSnackBarAlert = (
		severity: OverridableStringUnion<AlertColor, AlertPropsColorOverrides> | undefined,
		message: string,
		autoHideDuration: number = 2000,
	) => {
		runInAction(() => {
			this.snackBarProps = { open: true, severity, message, autoHideDuration };
		});
	};

	hideSnackBarAlert = () => {
		runInAction(() => {
			this.snackBarProps = undefined;
		});
	};

	showErrorAlertWithCode = (
		code?: string | number,
		message?: string,
		title?: string,
		autoHideDuration: number = 5000,
	) => {
		runInAction(() => {
			this.errorAlertWithCode = {
				open: true,
				code,
				message,
				autoHideDuration,
				title,
			};
		});
	};

	hideErrorAlertWithCode = () => {
		runInAction(() => {
			this.errorAlertWithCode = undefined;
		});
	};

	showSideBar = (): void => {
		runInAction(() => {
			if (this.sideBar.current) {
				this.sideBar.current.style.minWidth = "240px";
				this.sideBar.current.style.maxWidth = "unset";
				this.sideBar.current.style.display = "flex";
			}
		});
	};

	hideSideBar = (): void => {
		runInAction(() => {
			if (this.sideBar.current) {
				this.sideBar.current.style.maxWidth = "0";
				this.sideBar.current.style.minWidth = "0";
				this.sideBar.current.style.display = "none";
			}
		});
	};

	showBackButton = async (
		to?: string,
		navigateOptions?: NavigateOptions,
		text?: string,
		showWarning?: boolean,
		loadingWarning?: boolean,
		onWarningConfirm?: () => Promise<void>,
		currentAppName?: string,
		warningTitle?: string,
		warningDescription?: string,
		confirmText?: string,
		cancelText?: string,
		cancelClosesOnly?: boolean,
		hideWarningTitle?: boolean,
	): Promise<void> => {
		try {
			runInAction(() => {
				this.backButton = {
					to,
					navigateOptions,
					text,
					showWarning,
					loadingWarning,
					onWarningConfirm,
					currentAppName,
					warningTitle,
					warningDescription,
					confirmText,
					cancelText,
					cancelClosesOnly,
					hideWarningTitle,
				};
			});
		} catch (e) {
			return Promise.reject(e);
		}
	};

	hideBackButton = async (): Promise<void> => {
		try {
			runInAction(() => {
				this.backButton = undefined;
			});
		} catch (e) {
			return Promise.reject(e);
		}
	};

	showRefElement = (targetElement: keyof IUIStore): void => {
		runInAction(() => {
			if ((this as any)[targetElement].current) {
				(this as any)[targetElement].current.style.display = "flex";
			}
		});
	};

	hideRefElement = (targetElement: keyof IUIStore): void => {
		runInAction(() => {
			if ((this as any)[targetElement].current) {
				(this as any)[targetElement].current.style.display = "none";
			}
		});
	};

	shouldDisplayCustomerViewBanner = (): boolean => {
		return isBriaMember(this.rootStore.authStore.user) && !isBriaOrg();
	};

	showDialog = (targetDialog: keyof IUIStore): void => {
		runInAction(() => {
			(this as any)[targetDialog] = true;
		});
	};

	hideDialog = (targetDialog: keyof IUIStore): void => {
		runInAction(() => {
			(this as any)[targetDialog] = false;
		});
	};

	showSnackbar = (targetSnackbar: keyof IUIStore) => {
		runInAction(() => {
			(this as any)[targetSnackbar] = true;
		});
	};

	hideSnackbar = (targetSnackbar: keyof IUIStore) => {
		runInAction(() => {
			(this as any)[targetSnackbar] = false;
		});
	};

	hideAllFeedbackBoxes = (event: any): void => {
		const elements = document.querySelectorAll(".feedbackContainerRef") ?? [];
		elements.forEach((element) => {
			const closeButton = element.querySelector(".closeIcon");
			if (
				!(element as HTMLElement).contains(event.target) ||
				(closeButton && (closeButton as HTMLElement).contains(event.target))
			) {
				(element as HTMLElement).style.display = "none";
			}
		});
	};

	setProperty = async <K extends keyof IUIStore>(key: K, value: IUIStore[K]) => {
		runInAction(() => ((this as IUIStore)[key] = value));
	};

	hideDesignEditorContainer = (): void => {
		const container = this.rootStore.designEditorStore.containerRef?.current;
		if (container) {
			container.style.display = "none";
		}
	};

	showDesignEditorContainer = (): void => {
		const container = this.rootStore.designEditorStore.containerRef?.current;
		if (container) {
			container.style.display = "flex";
		}
	};
}
